/* globals msCrypto */
/**
 * This file is for using native crypto in the browser.
 *
 * @private
 */

const precond = require('precond');
const { Binary } = require('binary');
const Guard = require('precond');
const { TextEncoder } = require('text-encoding');
const utils = require('./utils');
const cryptoUtils = require('./crypto-utils');
const cryptoConfig = require('./crypto-config');
const Algorithms = require('../ciphers/algorithms');
const errors = require('./errors');

// Used to pass into native crypto functions
const METHODS = ['encrypt', 'decrypt'];

let nativeCrypto;
let subtle;

if (typeof crypto !== 'undefined') {
  subtle = crypto.subtle || crypto.webkitSubtle;
  nativeCrypto = crypto;
} else if (typeof msCrypto !== 'undefined') {
  subtle = require('./ie-native-shim');
  nativeCrypto = msCrypto;
}

// These are the required functions we need to support using the native crypto
if (!nativeCrypto || !subtle || !subtle.importKey || !subtle.encrypt || !subtle.decrypt) {
  nativeCrypto = undefined;
  subtle = undefined;
}

/**
 * Generate a random hex key
 * @return {String} - New key as a hex string
 */
function generateKey(length) {
  return randomBytesAsHex(length || 32);
}

/**
 * Generate an RSA key pair
 * @see    {@link https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/generateKey}
 * @param  {Number}  [size] in bits
 * @return {Promise<Object>} Promise of:
 *   {
 *     publicKey: PEM,
 *     privateKey: PEM
 *   }
 */
function generateKeyPair(size) {
  const minKeySize = cryptoConfig.getMinAsymmetricKeySizeBits();

  if (!cryptoUtils.isValidAsymmetricKeySize(size, minKeySize)) {
    throw new Error('Invalid key size requested');
  }

  const algoDomString = getAsymmetricAlgoDomString(true, size);

  return subtle
    .generateKey(algoDomString, true, METHODS)
    .then(function generatedKeys(keys) {
      return Promise.all([
        subtle.exportKey('spki', keys.publicKey),
        subtle.exportKey('pkcs8', keys.privateKey),
      ]);
    })
    .then(function returnHandler(keys) {
      const publicBase64String = Binary.fromArrayBuffer(keys[0]).asBuffer().toString('base64');
      const privateBase64String = Binary.fromArrayBuffer(keys[1]).asBuffer().toString('base64');
      return {
        publicKey: cryptoUtils.formatAsPem(publicBase64String, 'PUBLIC KEY'),
        privateKey: cryptoUtils.formatAsPem(privateBase64String, 'PRIVATE KEY'),
      };
    });
}

/**
 * Encrypt using a public key
 * @param  {Binary} payload   - Payload to encrypt
 * @param  {String} publicKey - PEM formatted public key
 * @return {Promise<Binary>}  - Encrypted payload
 */
function encryptWithPublicKey(payload, publicKey) {
  precond.checkIsObject(payload);
  precond.checkIsString(publicKey);

  const algoDomString = getAsymmetricAlgoDomString();

  // Web Crypto APIs don't work with PEM formatted strings
  publicKey = cryptoUtils.removePemFormatting(publicKey);

  const keyBuffer = Buffer.from(publicKey, 'base64').buffer;

  return subtle
    .importKey('spki', keyBuffer, algoDomString, false, ['encrypt'])
    .then(function keyHandler(cryptoKey) {
      return subtle.encrypt({ name: 'RSA-OAEP' }, cryptoKey, payload.asArrayBuffer());
    })
    .then(function returnHandler(result) {
      return Binary.fromArrayBuffer(result);
    });
}

/**
 * Generate a 16-byte initialization vector
 * @return {String}
 */
function generateInitializationVector(length) {
  return randomBytesAsHex(length || 16);
}

/**
 * Returns a promise to the encryption key as a binary string.
 *
 * Note: This function should almost never fail as it includes a fallback
 * if for some reason the native generate key fails.
 *
 * @param {Number} length - The key length, defaults to 256
 *
 * @returns {String} - The hex string.
 */
function randomBytesAsHex(length) {
  // Create a typed array of the correct length to fill
  const random = new Uint8Array(length);
  nativeCrypto.getRandomValues(random);

  const binary = Binary.fromArrayBuffer(random.buffer);

  // Convert the byte array to a hex string
  let key = '';
  const arr = binary.asByteArray();
  for (let i = 0; i < length; i++) {
    let str = arr[i].toString(16);
    if (str.length === 1) {
      str = `0${str}`;
    }
    key += str;
  }

  return key;
}

/**
 * Decrypt a public-key encrypted payload with a private key
 * @param  {Binary} encryptedPayload - Payload to decrypt
 * @param  {String} privateKey       - PEM formatted private key
 * @return {Promise<Binary>}         - Decrypted payload
 */
function decryptWithPrivateKey(encryptedPayload, privateKey) {
  precond.checkIsObject(encryptedPayload);
  precond.checkIsString(privateKey);

  const algoDomString = getAsymmetricAlgoDomString();

  // Web Crypto APIs don't work with PEM formatted strings
  let keyData = cryptoUtils.removePemFormatting(privateKey);

  keyData = Buffer.from(keyData, 'base64').buffer;

  return subtle
    .importKey('pkcs8', keyData, algoDomString, false, ['decrypt'])
    .then(function keyHandler(key) {
      return subtle.decrypt({ name: 'RSA-OAEP' }, key, encryptedPayload.asArrayBuffer());
    })
    .then(function returnHandler(payload) {
      const bufferView = new Uint8Array(payload);
      return Binary.fromArrayBuffer(bufferView.buffer);
    });
  // Calling code should catch
}

/**
 * Decrypt content synchronously
 * @param {Binary} payload - The payload to decrypt
 * @param {Binary} key     - The encryption key
 * @param {Binary} iv      - The initialization vector
 * @param {String} [algorithm] - The algorithm to use for encryption
 * @param {Binary} [authTag] - The authentication tag for authenticated crypto.
 * @return {Object} - The decrypted output
 * {
 *   payload: {Binary} - The decrypted payload.
 * }
 * @property
 */
function decrypt(payload, key, iv, algorithm, authTag) {
  if (utils.isStream(payload)) {
    return cryptoUtils.readStream(payload).then(function dataHandler(data) {
      return _doDecrypt(data, key, iv, algorithm, authTag);
    });
  }

  return _doDecrypt(payload, key, iv, algorithm, authTag);
}

/**
 * Encrypt content synchronously
 * @param {Binary} payload - The payload to encrypt
 * @param {Binary} key     - The encryption key
 * @param {Binary} iv      - The initialization vector
 * @param {String} [algorithm] - The algorithm to use for encryption
 * @return {Object} - The encrypted output
 * {
 *   payload: {Binary} - The encrypted payload.
 *   [authTag]: {Binary} The authentication tag generated.
 * }
 * @property
 */
function encrypt(payload, key, iv, algorithm) {
  return _doEncrypt(payload, key, iv, algorithm);
}

function _doEncrypt(payload, key, iv, algorithm, authTag) {
  Guard.checkIsDefAndNotNull(payload);
  Guard.checkIsDefAndNotNull(key);
  Guard.checkIsDefAndNotNull(iv);

  let payloadBuffer;
  if (payload.isBuffer && payload.isBuffer()) {
    payloadBuffer = payload.buffer ? payload.buffer : payload.asArrayBuffer();
  } else {
    payloadBuffer = payload.asArrayBuffer();
  }

  const algoDomString = getSymmetricAlgoDomString(algorithm);

  return _importKey(key, algoDomString)
    .then(function encryptPayload(importedKey) {
      algoDomString.iv = iv.asArrayBuffer();

      return subtle.encrypt(algoDomString, importedKey, payloadBuffer);
    })
    .then(function resolveCrypt(encrypted) {
      const result = {};
      if (algoDomString.name === 'AES-GCM') {
        result.payload = Binary.fromArrayBuffer(encrypted.slice(0, -16));
        result.authTag = Binary.fromArrayBuffer(encrypted.slice(-16));
      } else {
        result.payload = Binary.fromArrayBuffer(encrypted);
      }

      return result;
    });
}

function _doDecrypt(payload, key, iv, algorithm, authTag) {
  Guard.checkIsDefAndNotNull(payload);
  Guard.checkIsDefAndNotNull(key);
  Guard.checkIsDefAndNotNull(iv);

  let payloadBuffer;
  if (payload.isBuffer && payload.isBuffer()) {
    payloadBuffer = payload.buffer ? payload.buffer : payload.asArrayBuffer();
  } else {
    payloadBuffer = payload.asArrayBuffer();
  }

  // Concat the the auth tag to the payload for decryption
  if (authTag) {
    const authTagBuffer = authTag.asArrayBuffer();
    const gcmPayload = new Uint8Array(payloadBuffer.byteLength + authTagBuffer.byteLength);
    gcmPayload.set(new Uint8Array(payloadBuffer), 0);
    gcmPayload.set(new Uint8Array(authTagBuffer), payloadBuffer.byteLength);
    payloadBuffer = gcmPayload.buffer;
  }

  const algoDomString = getSymmetricAlgoDomString(algorithm);

  return _importKey(key, algoDomString)
    .then(function encryptPayload(importedKey) {
      algoDomString.iv = iv.asArrayBuffer();

      return (
        subtle
          .decrypt(algoDomString, importedKey, payloadBuffer)
          // Catching this error so we can specifically check for OperationError
          .catch(function errorHandler(err) {
            if (err.name === 'OperationError') {
              throw errors.IntegrityCompromisedError.create(err);
            }

            throw err;
          })
      );
    })
    .then(function resolveCrypt(encrypted) {
      return { payload: Binary.fromArrayBuffer(encrypted) };
    });
}

function _importKey(key, algorithm) {
  return subtle.importKey('raw', key.asArrayBuffer(), algorithm, true, METHODS);
}

/**
 * Get a DOMString representing the algorithm to use for a crypto
 * operation. Defaults to AES-CBC.
 * @param  {String|undefined} algorithm
 * @return {DOMString} Algorithm to use
 */
function getSymmetricAlgoDomString(algorithm) {
  let nativeAlgorithm = 'AES-CBC';
  if (algorithm === Algorithms.AES_256_GCM) {
    nativeAlgorithm = 'AES-GCM';
  }

  return {
    name: nativeAlgorithm,
  };
}

/**
 * Get a DOMString representing the algorithm to use for an
 * asymmetric key generation.
 * @param  {Boolean|undefined} isGenerate (only used for subtle.generateKey())
 * @param  {Number|undefined} size
 * @param  {String|undefined} algorithm
 * @param  {String|undefined} hash
 * @return {DOMString} Algorithm to use
 */
function getAsymmetricAlgoDomString(isGenerate, size, algorithm, hash) {
  const domString = {
    name: algorithm || 'RSA-OAEP',
    hash: {
      name: hash || 'SHA-1',
    },
  };

  if (isGenerate) {
    domString.modulusLength = size || cryptoConfig.getMinAsymmetricKeySizeBits();
    domString.publicExponent = new Uint8Array([0x01, 0x00, 0x01]); // 24 bit representation of 65537
  }

  return domString;
}

/**
 * Create a SHA256 hash. Code refrenced from MDN:
 * https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 * @param  {String}  content  String content
 * @return {String}  hash     Hex hash
 */
function sha256(content) {
  const buffer = new TextEncoder('utf-8').encode(content);
  return subtle.digest('SHA-256', buffer).then(function hashHandler(hashBuffer) {
    return Buffer.from(new Uint8Array(hashBuffer)).toString('hex');
  });
}

/**
 * Create an HMAC SHA256 hash
 * @param  {String} key     Key string
 * @param  {String} content Content string
 * @return {Promise<String>} Hex hash
 */
function hmac(key, content) {
  const contentBuffer = new TextEncoder('utf-8').encode(content);
  const keyBuffer = hex2Ab(key);

  return subtle
    .importKey(
      'raw',
      keyBuffer,
      {
        name: 'HMAC',
        hash: { name: 'SHA-256' },
      },
      true,
      ['sign', 'verify']
    )
    .then(function keyHandler(cryptoKey) {
      return subtle.sign('HMAC', cryptoKey, contentBuffer);
    })
    .then(function returnHandler(hashBuffer) {
      return Buffer.from(new Uint8Array(hashBuffer)).toString('hex');
    });
}

/**
 * Create an ArrayBuffer from a hex string.
 * https://developers.google.com/web/updates/2012/06/How-to-convert-ArrayBuffer-to-and-from-String?hl=en
 * @param  {String} hex - Hex string
 * @return {ArrayBuffer}
 */
function hex2Ab(hex) {
  const buffer = new ArrayBuffer(hex.length / 2);
  const bufferView = new Uint8Array(buffer);

  for (let i = 0; i < hex.length; i += 2) {
    bufferView[i / 2] = parseInt(hex.substr(i, 2), 16);
  }

  return buffer;
}

module.exports = {
  encrypt,
  decrypt,
  encryptWithPublicKey,
  decryptWithPrivateKey,
  generateKey,
  generateKeyPair,
  generateInitializationVector,
  isSupported: nativeCrypto !== undefined,
  sha256,
  hmac,
  hex2Ab,
  name: 'BrowserNativeCryptoService',
};
